import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeesApi, timeEntriesApi, schedulesApi, payrollApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  Clock,
  DollarSign,
  Calendar,
  UserCheck,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { format, isToday, parseISO } from 'date-fns';
import { useTimeFormat } from '../utils/timeFormat';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_staffease-2/artifacts/30upklx2_logo%20jf.png";

const Dashboard = () => {
  const { user, selectedLocation, locations } = useAuth();
  const fmtTime = useTimeFormat();
  const [payrollPeriod, setPayrollPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState({ totalHours: 0, totalCost: 0, employeeBreakdown: [] });

  useEffect(() => {
    if (selectedLocation) {
      loadData();
    }
  }, [selectedLocation, payrollPeriod]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emps, entries, scheds, payroll] = await Promise.all([
        employeesApi.getAll(selectedLocation),
        timeEntriesApi.getAll({ locationId: selectedLocation }),
        schedulesApi.getAll({ locationId: selectedLocation }),
        payrollApi.getSummary({ locationId: selectedLocation, period: payrollPeriod }),
      ]);
      
      setEmployees(emps);
      setTimeEntries(entries);
      setSchedules(scheds);
      setPayrollSummary(payroll);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  
  const todayEntries = timeEntries.filter((entry) => entry.date === todayStr);
  const clockedIn = todayEntries.filter((entry) => entry.clock_in && !entry.clock_out);
  
  const todaySchedules = schedules.filter((sched) => sched.date === todayStr);

  // Get upcoming shifts for today
  const upcomingShifts = todaySchedules.slice(0, 5).map((sched) => {
    const emp = employees.find((e) => e.id === sched.employee_id);
    return { ...sched, employee: emp };
  });

  const currentLocationName = selectedLocation === 'all' 
    ? 'All Locations' 
    : (locations || []).find((l) => l.id === selectedLocation)?.name;

  const stats = [
    {
      title: 'Total Employees',
      value: employees.length,
      icon: Users,
      color: 'bg-purple-500',
      change: '+2',
      changeType: 'positive',
    },
    {
      title: 'Clocked In Today',
      value: clockedIn.length,
      icon: UserCheck,
      color: 'bg-violet-500',
      subtitle: `of ${employees.length} employees`,
    },
    {
      title: 'Hours This Week',
      value: Math.round(payrollSummary.total_hours || payrollSummary.totalHours || 0),
      icon: Clock,
      color: 'bg-indigo-500',
      change: '+12%',
      changeType: 'positive',
    },
    {
      title: 'Payroll Cost',
      value: `$${(payrollSummary.total_cost || payrollSummary.totalCost || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-fuchsia-500',
      change: '-5%',
      changeType: 'negative',
    },
  ];

  const topEarners = useMemo(() => {
    const breakdown = payrollSummary.employee_breakdown || payrollSummary.employeeBreakdown || [];
    return [...breakdown]
      .sort((a, b) => (b.total_pay || b.totalPay || 0) - (a.total_pay || a.totalPay || 0))
      .slice(0, 3);
  }, [payrollSummary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <button onClick={loadData} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700" title="Refresh" data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <p className="text-slate-500 mt-1">
          Welcome back, {user?.name}. Here&apos;s what&apos;s happening at {currentLocationName}.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-sm text-slate-400 mt-1">{stat.subtitle}</p>
                  )}
                  {stat.change && (
                    <div className={`flex items-center gap-1 mt-2 text-sm ${
                      stat.changeType === 'positive' ? 'text-emerald-600' : 'text-red-500'
                    }`}>
                      {stat.changeType === 'positive' ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4" />
                      )}
                      <span>{stat.change} from last week</span>
                    </div>
                  )}
                </div>
                <div className={`${stat.color} p-3 rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule */}
        <Card className="lg:col-span-2 border-0 shadow-md">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Today&apos;s Schedule
              </CardTitle>
              <span className="text-sm text-slate-500">{format(today, 'EEEE, MMMM d')}</span>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingShifts.length > 0 ? (
              <div className="space-y-4">
                {upcomingShifts.map((shift) => (
                  <div
                    key={shift.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-700 font-semibold text-sm">
                          {shift.employee?.name?.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{shift.employee?.name}</p>
                        <p className="text-sm text-slate-500">{shift.employee?.position}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        {shift.start_time} - {shift.end_time}
                      </p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                        shift.shift_name === 'Morning' ? 'bg-amber-100 text-amber-700' :
                        shift.shift_name === 'Mid' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {shift.shift_name} Shift
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No shifts scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payroll Summary */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">
              Payroll Summary
            </CardTitle>
            <Tabs value={payrollPeriod} onValueChange={setPayrollPeriod} className="mt-2">
              <TabsList className="grid grid-cols-4 h-9">
                <TabsTrigger value="daily" className="text-xs">Day</TabsTrigger>
                <TabsTrigger value="weekly" className="text-xs">Week</TabsTrigger>
                <TabsTrigger value="monthly" className="text-xs">Month</TabsTrigger>
                <TabsTrigger value="annual" className="text-xs">Year</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-purple-600 font-medium">Total Labor Cost</p>
                <p className="text-2xl font-bold text-purple-700">
                  ${(payrollSummary.total_cost || payrollSummary.totalCost || 0).toLocaleString()}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Total Hours</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {Math.round(payrollSummary.total_hours || payrollSummary.totalHours || 0)}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Employees</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {(payrollSummary.employee_breakdown || payrollSummary.employeeBreakdown || []).length}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-3">Top Earners</p>
                <div className="space-y-2">
                  {topEarners.map((record) => (
                    <div key={record.employee?.id} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{record.employee?.name}</span>
                      <span className="text-sm font-medium text-slate-900">
                        ${(record.total_pay || record.totalPay || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="mt-6 border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Recent Time Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Clock In</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Clock Out</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {timeEntries.slice(0, 5).map((entry) => {
                  const emp = employees.find((e) => e.id === entry.employee_id);
                  return (
                    <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                            <span className="text-slate-600 font-medium text-xs">
                              {emp?.name?.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900">{emp?.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {entry.date ? format(parseISO(entry.date), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {entry.clock_in ? fmtTime(entry.clock_in) : '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {entry.clock_out ? fmtTime(entry.clock_out) : '-'}
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-900">
                        {(entry.hours_worked || 0).toFixed(2)}h
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {entry.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
